import { KlientVystupy } from '../affilmaster/stranky/KlientVystupy';
import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { getCrmPlatformSuggestions } from '../api/crm';
import {
  deleteKlient,
  getKlient,
  getKlientBilling,
  getKlientContacts,
  getKlientDocuments, refreshKlientDocuments,
  setKlientDriveFolder,
  getKlientReporting,
  updateKlient,
  uploadKlientLogo,
  getKlientDeals, addKlientDeal, updateKlientDeal, setKlientDealStatus,
  lookupKlientUol,
  updateKlientBilling,
  type KlientBilling,
  type KlientContact,
  type KlientDetail,
  type KlientInput,
  type KlientReporting,
  type KlientStatus,
} from '../api/klienti';
import { useAuth } from '../auth/AuthContext';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { HelpdeskTicketsEmbed } from '../helpdesk/HelpdeskTicketsEmbed';
import { KLIENT_STATUSES, KLIENT_STATUS_DEF, klientTabIds, type KlientiSpaceConfig, type KlientTabId } from './klienti-config';
import { fmtDateTime, fmtDay, fmtEur, fmtLongDay, klientInitials, webHost, webHref } from './klienti-format';
import { KlientKontakty } from './KlientKontakty';
import { KlientPoznamky } from './KlientPoznamky';
import { KlientDokumenty } from './KlientDokumenty';
import { KlientDealy, type KlientDealyApi } from './KlientDealy';
import { KlientLogoBgPicker } from './KlientLogoBgPicker';
import { composeLogoOnBackground, logoFrameAttrs, logoFrameStyle, readFileAsDataUrl } from './logo-compose';
import { StatusPill } from './klienti-pills';
import { confirmDialog, noticeDialog } from '../shell/dialogs';
import { Ellipsis } from 'lucide-react';
import { Button } from '../components/arc/button/button';
import { ArcPage, ArcPanel, ArcTabs } from '../components/arc/primitives/arc-primitives';

const DEALY_API: KlientDealyApi = { list: getKlientDeals, add: addKlientDeal, update: updateKlientDeal, setStatus: setKlientDealStatus };
const dealError = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message || fallback : fallback);

interface KlientDetailViewProps {
  cfg: KlientiSpaceConfig;
  id: number;
  onBack: () => void;
  onDeleted: () => void;
  onChanged: () => void;
  onOpenLead?: (leadId: number) => void;
}

type EditForm = {
  name: string; company: string; ic: string; vat: string; website: string; market: string;
  product: string; module: string; focus: string; platform: string; affiliatePlatform: string; monthlyFeeEur: string; contractFrom: string; contractTo: string; clientSince: string;
  status: KlientStatus; note: string;
  street: string; city: string; zip: string; billingEmails: string; uolContactId: string; uolCompanyName: string;
};

function UdajeItem({ label, value, code, link }: { label: string; value?: string | null; code?: string; link?: string | null }) {
  const has = code != null || (value != null && value !== '');
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">
        {code != null ? <code>{code}</code> : !has ? <span className="text-muted">-</span>
          : link ? <a href={link} target={link.startsWith('mailto') || link.startsWith('tel') ? undefined : '_blank'} rel="noreferrer">{value}</a> : value}
      </div>
    </div>
  );
}

function ReportingEmpty({ reporting }: { reporting: KlientReporting | null | undefined }) {
  return (
    <div className="klient-reporting-empty" role="status">
      <i data-feather="alert-triangle" />
      <div>
        <strong>Zdroj dat zatím není napojený</strong>
        <p>{reporting?.reason ?? 'Výkonová data tohoto klienta zatím nemají v Platform 1 napojený zdroj. Jakmile bude, zobrazí se zde bez dalších úprav karty.'}</p>
      </div>
    </div>
  );
}

export function KlientDetailView({ cfg, id, onBack, onDeleted, onChanged, onOpenLead }: KlientDetailViewProps) {
  const { me } = useAuth();
  const canManage = !!me?.permissionKeys?.includes(`${cfg.permissionModule}:manage`);
  const tabIds = klientTabIds(cfg);
  const [tab, setTab] = useTabParam<KlientTabId>('tab', tabIds, tabIds[0] ?? 'prehledy', false);
  const [detail, setDetail] = useState<KlientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reporting, setReporting] = useState<KlientReporting | null>(null);
  const [contacts, setContacts] = useState<KlientContact[] | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [platformSuggestions, setPlatformSuggestions] = useState<{ platforms: string[]; affiliatePlatforms: string[] }>({ platforms: [], affiliatePlatforms: [] });
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoNote, setLogoNote] = useState<string | null>(null);
  const [logoBg, setLogoBg] = useState<string | null>(null);
  const [billing, setBilling] = useState<KlientBilling | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const hasBilling = !!cfg.billing;

  useEffect(() => {
    if (!hasBilling) return;
    let cancelled = false;
    getKlientBilling(id)
      .then((b) => { if (!cancelled) setBilling(b); })
      .catch(() => { if (!cancelled) setBilling(null); });
    return () => { cancelled = true; };
  }, [id, reloadKey, hasBilling]);

  useEffect(() => {
    let cancelled = false;
    getKlient(id)
      .then((d) => { if (!cancelled) { setDetail(d); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Detail klienta selhal'); });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  const needsReporting = tab === 'prehledy' || tab === 'mesice' || tab === 'affiliates' || tab === 'doklady';
  useEffect(() => {
    if (!needsReporting || reporting) return;
    let cancelled = false;
    getKlientReporting(id)
      .then((r) => { if (!cancelled) setReporting(r); })
      .catch(() => { if (!cancelled) setReporting({ ready: false, reason: null, months: [], affiliates: [], invoices: [] }); });
    return () => { cancelled = true; };
  }, [id, needsReporting, reporting]);

  useEffect(() => {
    if (tab !== 'helpdesk') return;
    let cancelled = false;
    getKlientContacts(id)
      .then((c) => { if (!cancelled) setContacts(c); })
      .catch(() => { if (!cancelled) setContacts([]); });
    return () => { cancelled = true; };
  }, [id, tab, reloadKey]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);

  useFeather([detail, tab, editForm, editBusy, menuOpen, reporting, contacts, logoBusy, billing, lookupBusy]);

  const refresh = () => { setReloadKey((k) => k + 1); onChanged(); };

  function openEdit(d: KlientDetail) {
    setEditErr(null);
    setLogoBg(d.logoBg ?? null);
    setEditForm({
      name: d.name, company: d.company ?? '', ic: d.ic ?? '', vat: d.vat ?? '', website: d.website ?? '', market: d.market ?? cfg.markets[0]?.code ?? 'CZ',
      product: d.product ?? '', module: d.module ?? '', focus: d.focus ?? '', platform: d.platform ?? '', affiliatePlatform: d.affiliatePlatform ?? '', monthlyFeeEur: d.monthlyFeeEur != null ? String(d.monthlyFeeEur) : '',
      contractFrom: d.contractFrom ?? '', contractTo: d.contractTo ?? '', clientSince: d.clientSince ?? '', status: d.status, note: d.note ?? '',
      street: billing?.street ?? '', city: billing?.city ?? '', zip: billing?.zip ?? '', billingEmails: (billing?.billingEmails ?? []).join(', '),
      uolContactId: billing?.uolContactId ?? '', uolCompanyName: billing?.uolCompanyName ?? '',
    });
    if (!hasBilling) getCrmPlatformSuggestions().then(setPlatformSuggestions).catch(() => undefined);
  }

  async function lookupUol() {
    setLookupBusy(true);
    try {
      const r = await lookupKlientUol(id);
      if (r.found) refresh();
      void noticeDialog(r.found
        ? { title: 'Klient je napojený na UOL', message: `Kontakt ${r.contactId}${r.name ? ` (${r.name})` : ''}.` }
        : { title: 'Kontakt v UOL nenalezen', message: 'V UOL není kontakt se stejným IČ ani DIČ.' });
    } catch (e) {
      void noticeDialog({ title: 'Dohledání v UOL selhalo', message: e instanceof ApiError ? e.message : 'Zkuste to prosím znovu.' });
    } finally { setLookupBusy(false); }
  }

  async function saveEdit() {
    if (!editForm) return;
    if (editForm.name.trim().length < 2) { setEditErr('Název klienta musí mít aspoň 2 znaky'); return; }
    const feeRaw = editForm.monthlyFeeEur.trim().replace(',', '.');
    const fee = feeRaw === '' ? null : Number(feeRaw);
    if (fee != null && (!Number.isFinite(fee) || fee < 0)) { setEditErr('Měsíční částka není platné číslo'); return; }
    setEditBusy(true); setEditErr(null);
    try {
      const patch: Partial<KlientInput> = {
        name: editForm.name.trim(), company: editForm.company.trim() || null, ic: editForm.ic.trim() || null, vat: editForm.vat.trim() || null,
        website: editForm.website.trim() || null, market: editForm.market || null, product: editForm.product || null, module: editForm.module || null, focus: editForm.focus || null,
        platform: editForm.platform.trim() || null, affiliatePlatform: editForm.affiliatePlatform.trim() || null,
        monthlyFeeEur: fee, contractFrom: editForm.contractFrom || null, contractTo: editForm.contractTo || null, clientSince: editForm.clientSince || null,
        status: editForm.status, note: editForm.note.trim() || null,
      };
      await updateKlient(id, patch);
      if (hasBilling) {
        await updateKlientBilling(id, {
          street: editForm.street.trim() || null, city: editForm.city.trim() || null, zip: editForm.zip.trim() || null,
          billingEmails: editForm.billingEmails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean),
          uolContactId: editForm.uolContactId.trim() || null, uolCompanyName: editForm.uolCompanyName.trim() || null,
        });
      }
      setEditForm(null);
      refresh();
    } catch (e) { setEditErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); }
    finally { setEditBusy(false); }
  }

  async function uploadLogo(file: File) {
    const MAX = 2 * 1024 * 1024;
    if (file.size > MAX) {
      setEditErr(`Logo má ${(file.size / 1024 / 1024).toFixed(1)} MB, maximum jsou 2 MB.`);
      return;
    }
    setLogoBusy(true); setEditErr(null);
    setLogoNote(`Nahrávám ${file.name} (${Math.round(file.size / 1024)} kB)…`);
    try {
      const payload = logoBg ? await composeLogoOnBackground(file, logoBg) : { dataUrl: await readFileAsDataUrl(file), mime: file.type };
      await uploadKlientLogo(id, payload.dataUrl, payload.mime, logoBg);
      setLogoNote('Logo uloženo.');
      refresh();
      window.setTimeout(() => setLogoNote(null), 2500);
    } catch (e) {
      setLogoNote(null);
      setEditErr(e instanceof ApiError ? e.message : 'Nahrání loga selhalo');
    } finally { setLogoBusy(false); }
  }

  async function remove() {
    setMenuOpen(false);
    if (!detail) return;
    if (!(await confirmDialog({ title: 'Smazat klienta?', message: `„${detail.name}“ zmizí ze seznamu, kontakty a poznámky zůstanou v databázi.`, confirmLabel: 'Smazat', danger: true }))) return;
    try { await deleteKlient(id); onDeleted(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Smazání selhalo'); }
  }

  const d = detail;
  const marketLabel = (code: string | null) => cfg.markets.find((m) => m.code === code)?.label ?? code;
  const tabDef = cfg.tabs.find((t) => t.id === tab) ?? cfg.tabs[0]!;
  const since = fmtLongDay(d?.clientSince);
  const contactEmails = (contacts ?? []).map((c) => c.email?.trim() ?? '').filter((e) => e.length > 0);

  return (
    <ArcPage as="div" className="klient-detail-view klient-detail-arc">
      <div className="klient-detail-back-row"><Button type="button" variant="secondary" size="sm" onClick={onBack}>Zpět na seznam</Button></div>

      {error && <div className="alert alert-danger">{error}</div>}

      <ArcPanel className="klient-detail-hero">
        <div className="klient-detail-hero-row">
          <div className="klient-detail-identity">
            {d?.logo
              ? <img className="klient-detail-avatar" src={d.logo} alt={d.name} {...logoFrameAttrs(d.logoBg)} />
              : <div className="klient-detail-avatar klient-detail-initials">{klientInitials(d?.name ?? '?')}</div>}
            <div className="klient-detail-identity-copy">
              <div className="klient-detail-eyebrow">{cfg.entityEyebrow}</div>
              <h1 className="klient-detail-name">
                {d?.name ?? 'Načítám…'}
              </h1>
              {d?.company && d.company !== d.name && <p className="klient-detail-company">{d.company}</p>}
              <div className="klient-detail-meta">
                <span>#{id}</span>
                {d?.market && <><i>•</i><span>{marketLabel(d.market)}</span></>}
                {d && <StatusPill status={d.status} />}
                {d?.product && <><i>•</i><span>{d.product}</span></>}
              </div>
              <div className="klient-detail-since">Klient od <strong>{since ?? '-'}</strong></div>
            </div>
          </div>
          <div className="klient-detail-actions">
            {canManage && (
              <Button type="button" variant="secondary" size="sm" aria-label="Další možnosti" title="Další možnosti" onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}><Ellipsis size={18} aria-hidden="true" /></Button>
            )}
            {menuOpen && (
              <div className="klient-detail-action-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                <Button type="button" variant="danger" size="sm" role="menuitem" onClick={remove}>Smazat klienta</Button>
              </div>
            )}
            {canManage && <Button type="button" size="sm" disabled={!d} onClick={() => d && openEdit(d)}>Upravit</Button>}
          </div>
        </div>
      </ArcPanel>

      <ArcTabs label="Karty klienta" items={cfg.tabs.map((t) => {
          const cnt = t.id === 'kontakty' ? (d?.contactsCount ?? 0) : t.id === 'poznamky' ? (d?.notesCount ?? 0) : 0;
          return { value: t.id, label: <>{t.label}{cnt > 0 && <span className="klient-detail-tab-count">{cnt}</span>}</> };
        })} value={tab} onChange={setTab} className="klient-detail-tabs" />

      <ArcPanel className="klient-detail-content">
          {!d && !error && <div className="prototype-loading" aria-label="Načítám"><span /><span /><span /><span /></div>}

          {d && tab === 'prehledy' && (
            <>
              <h2 className="klient-detail-section-title">Základní přehled</h2>
              <div className="klient-detail-overview-grid">
                {[
                  ['Stav', KLIENT_STATUS_DEF[d.status].label],
                  ['Produkt', d.product ?? '-'],
                  ['Modul', d.module ?? '-'],
                  ['Měsíčně', fmtEur(d.monthlyFeeEur)],
                  ['Klient od', fmtDay(d.clientSince)],
                  ['Kontakty', String(d.contactsCount)],
                  ['Poznámky', String(d.notesCount)],
                ].map(([label, value]) => <ArcPanel key={label} className="klient-detail-overview-item"><span>{label}</span><strong>{value}</strong></ArcPanel>)}
              </div>
              <div className="klient-detail-section-head"><h2>Výkon</h2><p>{tabDef.description}</p></div>
              <ReportingEmpty reporting={reporting} />
            </>
          )}

          {d && tab === 'udaje' && (
            <div className="udaje-cols">
              <div>
                <div className="udaje-section">
                  <div className="udaje-section-head">Identifikace</div>
                  <div className="udaje-card">
                    <UdajeItem label="Název klienta" value={d.name} />
                    <UdajeItem label="Společnost" value={d.company} />
                    <UdajeItem label="ID" code={String(d.id)} />
                    <UdajeItem label="IČ" value={d.ic} />
                    <UdajeItem label="DIČ" value={d.vat} />
                    <UdajeItem label="Země" value={marketLabel(d.market)} />
                    <UdajeItem label="Web" value={webHost(d.website)} link={d.website ? webHref(d.website) : null} />
                  </div>
                </div>
                <div className="udaje-section">
                  <div className="udaje-section-head">Původ</div>
                  <div className="udaje-card">
                    {!hasBilling && (
                      <div className="udaje-item">
                        <div className="udaje-item-label">CRM lead</div>
                        <div className="udaje-item-value">
                          {d.leadId == null ? <span className="text-muted">založen ručně</span>
                            : onOpenLead ? <a href="#" onClick={(e) => { e.preventDefault(); onOpenLead(d.leadId!); }}>Lead #{d.leadId}</a>
                            : <code>#{d.leadId}</code>}
                        </div>
                      </div>
                    )}
                    <UdajeItem label="Klient od" value={fmtDay(d.clientSince)} />
                    <UdajeItem label="Vytvořeno" value={fmtDateTime(d.createdAt)} />
                    <UdajeItem label="Poslední změna" value={fmtDateTime(d.updatedAt)} />
                  </div>
                </div>
              </div>
              <div>
                {hasBilling ? (
                  <div className="udaje-section">
                    <div className="udaje-section-head">Fakturace</div>
                    <div className="udaje-card">
                      <UdajeItem label="Ulice" value={billing?.street} />
                      <UdajeItem label="Město" value={billing?.city} />
                      <UdajeItem label="PSČ" value={billing?.zip} />
                      <UdajeItem label="Fakturační e-maily" value={billing?.billingEmails.join(', ')} />
                      <UdajeItem label="Měna" value={billing?.billingCurrency} />
                      <UdajeItem label="Kontakt v UOL" value={billing?.uolContactId} />
                      <UdajeItem label="Název v UOL" value={billing?.uolCompanyName} />
                      <UdajeItem label="Stav" value={KLIENT_STATUS_DEF[d.status].label} />
                    </div>
                    {canManage && (
                      <button type="button" className="btn prototype-secondary-btn mt-2" disabled={lookupBusy} onClick={() => void lookupUol()}>
                        {lookupBusy ? 'Hledám v UOL…' : 'Dohledat kontakt v UOL podle IČ'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="udaje-section">
                    <div className="udaje-section-head">Produkt a tarif</div>
                    <div className="udaje-card">
                      <UdajeItem label="Produkt" value={d.product} />
                      <UdajeItem label="Modul" value={d.module} />
                      <UdajeItem label="Zaměření" value={d.focus} />
                      <UdajeItem label="Kasino platforma" value={d.platform} />
                      <UdajeItem label="Affiliate platforma" value={d.affiliatePlatform} />
                      <UdajeItem label="Měsíční částka" value={d.monthlyFeeEur != null ? fmtEur(d.monthlyFeeEur) : null} />
                      <UdajeItem label="Smlouva od" value={d.contractFrom ? fmtDay(d.contractFrom) : null} />
                      <UdajeItem label="Smlouva do" value={d.contractTo ? fmtDay(d.contractTo) : null} />
                      <UdajeItem label="Stav" value={KLIENT_STATUS_DEF[d.status].label} />
                    </div>
                  </div>
                )}
                {d.note && (
                  <div className="udaje-section">
                    <div className="udaje-section-head">Interní poznámka</div>
                    <div className="udaje-card">
                      <div className="udaje-item"><div className="udaje-item-value" style={{ whiteSpace: 'pre-wrap' }}>{d.note}</div></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {d && tab === 'kontakty' && <KlientKontakty id={id} canManage={canManage} onChanged={refresh} mailingSpace={cfg.mailingSpace} />}
          {d && tab === 'poznamky' && <KlientPoznamky id={id} canManage={canManage} onChanged={refresh} />}
          {d && tab === 'dealy' && (cfg.billing
            ? <KlientDealy id={id} defaultCurrency={billing?.billingCurrency ?? cfg.dealCurrency} canManage={canManage} api={DEALY_API} errText={dealError} kinds={['fix']} fixProduct={cfg.billing.fixProduct} />
            : <KlientDealy id={id} defaultCurrency={cfg.dealCurrency} canManage={canManage} api={DEALY_API} errText={dealError} />)}

          {d && (tab === 'mesice' || tab === 'affiliates' || tab === 'doklady') && (
            <section>
              <div className="prototype-panel-head"><div><h2>{tabDef.label}</h2><p>{tabDef.description}</p></div></div>
              <ReportingEmpty reporting={reporting} />
            </section>
          )}

          {tab === 'vystupy' && (
            <section>
              <div className="prototype-panel-head"><div><h2>Výstupy</h2><p>{tabDef.description}</p></div></div>
              <KlientVystupy partnerId={id} />
            </section>
          )}

          {tab === 'dokumenty' && (
            <KlientDokumenty canManage={canManage} load={() => getKlientDocuments(id)} refresh={() => refreshKlientDocuments(id)} saveFolder={(url) => setKlientDriveFolder(id, url)} />
          )}

          {d && tab === 'helpdesk' && (
            <section>
              <div className="prototype-panel-head"><div><h2>Helpdesk</h2><p>{tabDef.description}</p></div></div>
              {contacts == null && <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}
              {contacts != null && contactEmails.length === 0 && <div className="prototype-empty-contact">Klient nemá kontaktní osobu s e-mailem, tickety nejde přiřadit.</div>}
              {contactEmails.map((email) => (
                <div className="klient-helpdesk-contact" key={email}>
                  <h3>{email}</h3>
                  <HelpdeskTicketsEmbed email={email} />
                </div>
              ))}
            </section>
          )}
      </ArcPanel>

      {editForm && (
        <div className="prototype-modal-backdrop aff-editor-backdrop" onClick={() => !editBusy && setEditForm(null)}>
          <form
            className="aff-editor-modal aff-editor-modal-client aff-editor-modal-klient"
            role="dialog"
            aria-modal="true"
            aria-busy={editBusy}
            aria-labelledby="klient-editor-title"
            onSubmit={(e) => { e.preventDefault(); void saveEdit(); }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="aff-editor-head">
              <div>
                <div className="aff-editor-kicker"><i data-feather="briefcase" /> {cfg.eyebrow} · klient</div>
                <h2 id="klient-editor-title">Upravit klienta</h2>
                <p>{hasBilling ? 'Firemní a fakturační údaje.' : 'Firemní údaje, tarif a smlouva.'}</p>
              </div>
              <button type="button" className="aff-editor-close" aria-label="Zavřít" disabled={editBusy} onClick={() => setEditForm(null)}><i data-feather="x" /></button>
            </header>

            <div className="aff-editor-body">
              {editErr && <div className="aff-editor-alert" role="alert"><i data-feather="alert-circle" /><span>{editErr}</span></div>}

              <div className="aff-editor-layout">
                <section className="aff-editor-main">
                  <div className="aff-editor-section-head">
                    <h3>Firemní údaje</h3>
                    <p>Údaje používané v seznamu, přehledech a dokladech.</p>
                  </div>
                  <div className="aff-editor-grid">
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Název klienta</span>
                      <input className="aff-editor-control" autoFocus value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Společnost</span>
                      <input className="aff-editor-control" value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} placeholder="právní název firmy" />
                    </label>
                    <label className="aff-editor-field">
                      <span>IČ</span>
                      <input className="aff-editor-control" inputMode="numeric" value={editForm.ic} onChange={(e) => setEditForm({ ...editForm, ic: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>DIČ</span>
                      <input className="aff-editor-control" value={editForm.vat} onChange={(e) => setEditForm({ ...editForm, vat: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>Země</span>
                      <select className="aff-editor-control" value={editForm.market} onChange={(e) => setEditForm({ ...editForm, market: e.target.value })}>
                        {cfg.markets.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                      </select>
                    </label>
                    <label className="aff-editor-field">
                      <span>Klient od</span>
                      <input className="aff-editor-control" type="date" value={editForm.clientSince} onChange={(e) => setEditForm({ ...editForm, clientSince: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Web</span>
                      <div className="aff-editor-input-icon"><i data-feather="globe" /><input className="aff-editor-control" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="www.example.com" /></div>
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Interní poznámka</span>
                      <textarea className="aff-editor-control" rows={3} value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
                    </label>
                  </div>
                </section>

                {hasBilling ? (
                <aside className="aff-editor-aside">
                  <div className="aff-editor-section-head">
                    <h3>Fakturace</h3>
                    <p>Adresa, příjemci faktur a napojení na UOL.</p>
                  </div>
                  <div className="aff-editor-grid">
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Ulice</span>
                      <input className="aff-editor-control" value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>Město</span>
                      <input className="aff-editor-control" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>PSČ</span>
                      <input className="aff-editor-control" value={editForm.zip} onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Fakturační e-maily</span>
                      <input className="aff-editor-control" value={editForm.billingEmails} onChange={(e) => setEditForm({ ...editForm, billingEmails: e.target.value })} placeholder="oddělené čárkou" />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Kontakt v UOL</span>
                      <input className="aff-editor-control" value={editForm.uolContactId} onChange={(e) => setEditForm({ ...editForm, uolContactId: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Název v UOL</span>
                      <input className="aff-editor-control" value={editForm.uolCompanyName} onChange={(e) => setEditForm({ ...editForm, uolCompanyName: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Stav</span>
                      <select className="aff-editor-control" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as KlientStatus })}>
                        {KLIENT_STATUSES.map((s) => <option key={s} value={s}>{KLIENT_STATUS_DEF[s].label}</option>)}
                      </select>
                    </label>
                  </div>
                  <p className="aff-editor-footnote">Měsíční částky se spravují na tabu Dealy.</p>
                </aside>
                ) : (
                <aside className="aff-editor-aside">
                  <section className="aff-editor-side-block" aria-labelledby="klient-logo-heading">
                    <div className="aff-editor-side-title"><div><h3 id="klient-logo-heading">Logo</h3><p>PNG, JPG, WebP nebo SVG</p></div></div>
                    <div className="aff-editor-logo-row">
                      <div className={`aff-editor-logo-preview ${d?.logoBg ? 'has-bg' : ''}`} style={logoFrameStyle(d?.logoBg)}>
                        {d?.logo ? <img src={d.logo} alt={`Logo ${editForm.name || d.name}`} /> : <span>{klientInitials(editForm.name || d?.name || '?')}</span>}
                      </div>
                      <label className={`aff-editor-upload ${logoBusy || editBusy ? 'is-disabled' : ''}`}>
                        <span className="aff-editor-upload-icon" aria-hidden="true">
                          <span className="aff-editor-spinner" style={{ display: logoBusy ? undefined : 'none' }} />
                          <span style={{ display: logoBusy ? 'none' : undefined }}><i data-feather="upload-cloud" /></span>
                        </span>
                        {' '}{logoBusy ? 'Nahrávám…' : d?.logo ? 'Změnit logo' : 'Nahrát logo'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden disabled={logoBusy || editBusy}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); e.currentTarget.value = ''; }} />
                      </label>
                      {logoNote && <small className="aff-editor-help aff-logo-note">{logoNote}</small>}
                    </div>
                    <KlientLogoBgPicker value={logoBg} onChange={setLogoBg} disabled={logoBusy || editBusy} />
                  </section>

                  <section className="aff-editor-side-block" aria-labelledby="klient-tarif-heading">
                  <div className="aff-editor-side-title"><div><h3 id="klient-tarif-heading">Produkt a tarif</h3><p>Co klient používá a za kolik.</p></div></div>
                  <div className="aff-editor-grid">
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Produkt</span>
                      <select className="aff-editor-control" value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })}>
                        <option value="">- nevybráno -</option>
                        {[...new Set([...cfg.products, ...(editForm.product ? [editForm.product] : [])])].map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </label>
                    {cfg.modules.length > 0 && (
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Modul</span>
                      <select className="aff-editor-control" value={editForm.module} onChange={(e) => setEditForm({ ...editForm, module: e.target.value })}>
                        <option value="">- nevybráno -</option>
                        {[...new Set([...cfg.modules, ...(editForm.module ? [editForm.module] : [])])].map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </label>
                    )}
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Zaměření</span>
                      <select className="aff-editor-control" value={editForm.focus} onChange={(e) => setEditForm({ ...editForm, focus: e.target.value })}>
                        <option value="">- nevybráno -</option>
                        {[...new Set([...cfg.focuses, ...(editForm.focus ? [editForm.focus] : [])])].map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </label>
                    {!hasBilling && (
                      <>
                        <label className="aff-editor-field aff-editor-field-full">
                          <span>Kasino platforma</span>
                          <input className="aff-editor-control" value={editForm.platform} onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })} placeholder="např. SoftSwiss, EveryMatrix, vlastní" list="klient-platform-options" />
                          <datalist id="klient-platform-options">{platformSuggestions.platforms.map((p) => <option key={p} value={p} />)}</datalist>
                        </label>
                        <label className="aff-editor-field aff-editor-field-full">
                          <span>Affiliate platforma</span>
                          <input className="aff-editor-control" value={editForm.affiliatePlatform} onChange={(e) => setEditForm({ ...editForm, affiliatePlatform: e.target.value })} placeholder="např. Post Affiliate Pro, Scaleo, žádná" list="klient-affiliate-platform-options" />
                          <datalist id="klient-affiliate-platform-options">{platformSuggestions.affiliatePlatforms.map((p) => <option key={p} value={p} />)}</datalist>
                        </label>
                      </>
                    )}
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Měsíční částka (EUR)</span>
                      <input className="aff-editor-control" inputMode="decimal" value={editForm.monthlyFeeEur} onChange={(e) => setEditForm({ ...editForm, monthlyFeeEur: e.target.value })} placeholder="0" />
                    </label>
                    <label className="aff-editor-field">
                      <span>Smlouva od</span>
                      <input className="aff-editor-control" type="date" value={editForm.contractFrom} onChange={(e) => setEditForm({ ...editForm, contractFrom: e.target.value })} />
                    </label>
                    <label className="aff-editor-field">
                      <span>Smlouva do</span>
                      <input className="aff-editor-control" type="date" value={editForm.contractTo} onChange={(e) => setEditForm({ ...editForm, contractTo: e.target.value })} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Stav</span>
                      <select className="aff-editor-control" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as KlientStatus })}>
                        {KLIENT_STATUSES.map((s) => <option key={s} value={s}>{KLIENT_STATUS_DEF[s].label}</option>)}
                      </select>
                    </label>
                  </div>
                  <p className="aff-editor-footnote">Kontaktní osoby se spravují samostatně na tabu Kontakty.</p>
                  </section>
                </aside>
                )}
              </div>
            </div>

            <footer className="aff-editor-footer">
              <span>Změny se projeví v celém {cfg.title.toLowerCase() === 'klienti' ? 'Space' : cfg.title}.</span>
              <div>
                <button type="button" className="aff-editor-button is-secondary" disabled={editBusy} onClick={() => setEditForm(null)}>Zrušit</button>
                <button type="submit" className="aff-editor-button is-primary" disabled={editBusy}>{editBusy ? <><span className="aff-editor-spinner" /> Ukládám…</> : 'Uložit změny'}</button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </ArcPage>
  );
}

